import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Section,
  Cell,
  Spinner,
  Placeholder,
  Button,
  Text,
} from '@telegram-apps/telegram-ui';
import { StageProgress } from '../components/StageProgress';
import { ApprovalButtons } from '../components/ApprovalButtons';
import { RatingThankYou } from '../components/RatingModal';
import { CommentThread } from '../components/CommentThread';
import { useDeal, useDealAction, useComments } from '../hooks/useDeals';
import { ContactManagerLink } from '../components/ContactManagerLink';
import { useTelegram } from '../hooks/useTelegram';
import type { ApprovalAction } from '../types';

export function DealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { setBackButtonVisible } = useTelegram();
  const numDealId = parseInt(dealId || '0', 10);

  const { deal, isLoading, error, refresh } = useDeal(numDealId);
  const { executeAction, isLoading: actionLoading, error: actionError } = useDealAction();
  const { comments, isLoading: commentsLoading, error: commentsError, addComment } = useComments(numDealId);
  const [thankYouRating, setThankYouRating] = useState<number | null>(null);

  // Кнопка "Назад" в Telegram
  useEffect(() => {
    setBackButtonVisible(true, () => navigate(-1));
    return () => setBackButtonVisible(false);
  }, [setBackButtonVisible, navigate]);

  /**
   * Обработка действия клиента
   */
  async function handleAction(
    action: ApprovalAction,
    body?: { message?: string; rating?: number; ratingComment?: string }
  ): Promise<boolean> {
    const success = await executeAction(numDealId, action, body);
    if (success) {
      // Обновляем данные сделки после действия
      await refresh();
    }
    return success;
  }

  // Форматирование даты
  function formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  }

  // ==========================================
  // Состояния загрузки / ошибки
  // ==========================================

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner size="m" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <Placeholder
        header="Ошибка"
        description={error || 'Сделка не найдена'}
        action={
          <Button size="m" onClick={() => navigate('/')}>
            К списку заявок
          </Button>
        }
      />
    );
  }

  // ==========================================
  // Основной рендер
  // ==========================================

  return (
    <div style={{ paddingBottom: '20px' }}>
      {/* Заголовок */}
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{
            background: deal.categoryId === 0 ? '#2196F3' : '#FF9800',
            color: '#fff',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
          }}>
            {deal.funnelName}
          </span>
          <span style={{ color: 'var(--tgui--hint_color)', fontSize: '13px' }}>
            #{deal.id}
          </span>
        </div>
        <Text weight="1" style={{ fontSize: '20px' }}>
          {deal.title}
        </Text>
      </div>

      {/* Прогресс-бар стадий */}
      <StageProgress
        stages={deal.stageProgress}
        isFail={deal.isFail}
        failStageName={deal.isFail ? deal.clientStageName : undefined}
      />

      {/* Информация о сделке */}
      <Section header="Информация">
        <Cell subtitle="Стадия">
          <Text weight="2" style={{ color: deal.isFail ? '#ff3b30' : 'var(--tgui--text_color)' }}>
            {deal.clientStageName}
          </Text>
        </Cell>
        <Cell subtitle="Дата создания">
          {formatDate(deal.dateCreate)}
        </Cell>
        {deal.opportunity > 0 && (
          <Cell subtitle="Стоимость">
            {deal.opportunity.toLocaleString('ru-RU')} руб.
          </Cell>
        )}
        {deal.billedTime && (
          <Cell subtitle="Актируемое время">
            {deal.billedTime} ч.
          </Cell>
        )}
      </Section>

      {/* Привязанные задачи */}
      {deal.tasks.length > 0 && (
        <Section header="Задачи">
          {deal.tasks.map((task) => (
            <Cell
              key={task.id}
              subtitle={task.taskType || 'Задача'}
              after={
                <span style={{
                  fontSize: '12px',
                  color: task.status === 5 ? '#4caf50' : task.status === 2 ? '#2196f3' : 'var(--tgui--hint_color)',
                }}>
                  {task.status === 5 ? 'Закрыта' : task.status === 2 ? 'В работе' : `Статус: ${task.status}`}
                </span>
              }
            >
              {task.title}
            </Cell>
          ))}
        </Section>
      )}

      {/* Кнопки действий */}
      {deal.clientAction && deal.clientAction !== 'review' && (
        <Section header="Требуется ваше решение">
          <ApprovalButtons
            deal={deal}
            onAction={handleAction}
            isLoading={actionLoading}
            error={actionError}
            onRatingSuccess={(rating) => setThankYouRating(rating)}
          />
        </Section>
      )}

      {/* Комментарии */}
      <Section>
        <CommentThread
          comments={comments}
          isLoading={commentsLoading}
          error={commentsError}
          onAddComment={addComment}
        />
      </Section>

      <div style={{ padding: '8px 16px 16px' }}>
        <ContactManagerLink />
      </div>

      {/* Экран благодарности после оценки (вне ApprovalButtons чтобы не пропал при рефреше) */}
      {thankYouRating !== null && (
        <RatingThankYou
          rating={thankYouRating}
          onClose={() => setThankYouRating(null)}
        />
      )}
    </div>
  );
}
